import APIBase from "./APIBase";

/**
* @typedef RadiusAddress
* @property {string} Username
* @property {string} Address
* @property {string} City
* @property {string} Zip
* @property {string} Country
* @property {string} State
*/

/**
 * @typedef CustomerRadiusDetails
 * @property {string} ServiceName
 * @property {number} ServiceId
 * @property {string} IPAddress
 * @property {string} AccountType
 * @property {string} ExpiryDate
 * @property {number} CurrentUsage
 * @property {number} Price,
 * @property {RadiusAddress} Address
 */

/**
 * @typedef Customer
 * @property {string} Id
 * @property {string} FirstName
 * @property {string} Surname
 * @property {string} Email
 * @property {string} CellphoneNumber
 * @property {string} LanguagePreference
 * @property {string} EmailPreference
 * @property {string} IDNumber
 * @property {string} RadiusUsername
 * @property {boolean} SetOwnPassword
 * @property {boolean} HasPin
 * @property { any[] } SuccessfulRecharges
 */

/**
 * @typedef CustomerDetails
 * @property {Customer} Customer
 * @property {CustomerRadiusDetails} RadiusDetails
 */

/**
 * @typedef Document
 * @property {string} Id
 * @property {string} FileName
 * @property {string} Content
 */

/**
 * @typedef CustomerRegistration
 * @property {Customer} Customer
 * @property {Document} IDDocument
 * @property {Document} ProofOfAddress
 */

/**
 * @typedef UpdateProfile
 * @property {string} Email
 * @property {string} CellphoneNumber
 * @property {string} CurrentPin
 * @property {string} NewPin
 * @property {string} NewPinConfirm
 * @property {string} CommunicationPreference
 * @property {string} Language
 */

/**
 * @typedef CustomersService
 * @type {CustomersService}
 */
export class CustomersService extends APIBase {
/**
     * @param {string} apiUrl
     * @param { import("../Stores/SessionStore").SessionStore } sessionStore
     **/
	constructor(apiUrl, sessionStore) {
		super(apiUrl + "Customers", sessionStore);
	}

	/**
     * @returns { import("./APIBase").EnumerableRequestResult<RadiusAddress> }
     */
     async SearchAddress(searchString) {
        return this.Get(`SearchAddress?searchString=${encodeURIComponent(searchString)}`);
    }

	/**
     * @returns { import("./APIBase").RequestResult<CustomerDetails> }
     */
     async GetCustomerDetails() {
        return this.Get(`GetCustomerDetails`);
    }

	/**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
     async RegisterCustomer(customerRegistration) {
        return this.Post(`RegisterCustomer`, customerRegistration);
    }

    /**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async ResubmitCustomerDocument(customerId,customerRegistration) {
        return this.Post(`ResubmitCustomerDocument?customerId=${encodeURIComponent(customerId)}`, customerRegistration);
    }

	/**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async InitiateLogin(phoneNumber) {
       return this.Post(`InitiateLogin`, { PhoneNumber: phoneNumber });
   }

    async RequestLoginOtp(phoneNumber) {
       return this.Post(`RequestLoginOtp`, { PhoneNumber: phoneNumber });
   }

    async LoginWithPin(phoneNumber, pin) {
       return this.Post(`LoginWithPin`, { PhoneNumber: phoneNumber, Pin: pin });
   }

    async LoginWithOtp(phoneNumber, otp) {
       return this.Post(`LoginWithOtp`, { PhoneNumber: phoneNumber, Otp: otp });
    }

	/**
	 * @param { UpdateProfile } profile
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
     async UpdateProfile(profile) {
        return this.Post(`UpdateProfile`, profile);
    }

    async RequestChangeNumberOtp(newPhoneNumber) {
        return this.Post(`RequestChangeNumberOtp`, { NewPhoneNumber: newPhoneNumber });
    }

    async ChangePhoneNumber(newPhoneNumber, otp) {
        return this.Post(`ChangePhoneNumber`, { NewPhoneNumber: newPhoneNumber, Otp: otp });
    }

    async DeleteOwnAccount() {
        return this.Post(`DeleteOwnAccount`);
    }

	/**
	 * @param { string } customerId
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
     async CancelSubscription(customerId) {
        return this.Post(`CancelSubscription?customerId=${customerId}`);
    }
    
	/**
	 * @param { SendRouterNeededEmail } customer
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async SendRouterNeededEmail(addressId, customer) {
        return this.Post(`SendRouterNeededEmail?addressId=${addressId}`, customer);
    }

    /**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async SubmitDocuments(customerId, customerRegistration) {
        return this.Post(`SubmitDocuments?customerId=${encodeURIComponent(customerId)}`, customerRegistration);
    }

    /**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async UpdateWIFIPassword(newWifiName, newPassword, newPasswordConfirmation) {
        return this.Post(`UpdateWIFIPassword?newWifiName=${encodeURIComponent(newWifiName)}&newPassword=${encodeURIComponent(newPassword)}&newPasswordConfirmation=${encodeURIComponent(newPasswordConfirmation)}`);
    }

    /**
     * @returns { import("./APIBase").RequestResult<boolean> }
     */
    async ActivateIntroPackage() {
        return this.Post(`ActivateIntroPackage`);
    }

    async ClearDocuments(customerId) {
        return this.Post(`ClearDocuments?customerId=${encodeURIComponent(customerId)}`);
    }

    async AddChurnedServiceAgent(customerId, salesAgentId) {
        return this.Post(`AddChurnedServiceAgent?customerId=${encodeURIComponent(customerId)}&salesAgentId=${encodeURIComponent(salesAgentId)}`);
    }

    async GetPurchaseHistory(customerId) {
        return this.Get(`GetPurchaseHistory?customerId=${encodeURIComponent(customerId)}`);
    }
}